import type { useSearch } from "@utils/usePagination";
import {
	type Accessor,
	createContext,
	type Resource,
	type Setter,
} from "solid-js";
import type { SetStoreFunction, Store } from "solid-js/store";
import type {
	BoardClipResponse,
	ListBoardsResponse,
	VideoDetailResponse,
} from "@/client";
import type { AddBoardFormData } from "~/modules/askgvt/boards/list/components/NewBoardButton";
import type {
	BoardInfo,
	MoveClipTrim,
	SaveClipFailureReason,
} from "./addToBoardSave";

export const LAST_BOARD_ID_KEY = "last_board_id";
export {
	type BoardInfo,
	buildAddClipBody,
	clampClipTrim,
	isClipUnsaved,
	type MoveClipTrim,
	NEW_CLIP_PLACEHOLDER_ID,
	type SaveClipFailureReason,
	saveClipErrorTitle,
} from "./addToBoardSave";

/** Passed to `onUpdateBoardClip` after a successful save (ids captured before `setBoard` mutates context). */
export type UpdateBoardClipDonePayload = {
	sourceBoardId: string | undefined;
	targetBoardId: string | null;
	clipId: string | undefined;
	moveClipTrim: MoveClipTrim;
};

export type StoreType = {
	selectedBoardId: string | null;
	board: BoardInfo | null;
	clip: BoardClipResponse | null;
	video: VideoDetailResponse;
	clipAddSource: ClipAddSource | null;
	/** Persisted trim while moving clips; keyed by clip `clip.id`. */
	moveClipTrimByItemId: Record<string, MoveClipTrim>;
	search: Pick<
		ReturnType<typeof useSearch>,
		"search" | "searchInput" | "handleSearch" | "handleClear"
	>;
	currentPanel: Accessor<number>;
	formData: Store<AddBoardFormData>;
	errors: Accessor<Record<string, string[]>>;
	isLoading: boolean;
	/** Trim for the current `boardItem`, or defaults from the item / API. */
	moveClipTrim: MoveClipTrim;
	/** Whether the clip must be created via add (not move/update). */
	isClipUnsaved: boolean;
	/** Board picker list for {@link MoveBoardPanel}; refetches via boards store invalidation. */
	boardsList: Resource<ListBoardsResponse>;
};

/** Bounds for addClipToBoard when duplicating into extra boards (no full VideoDetailResponse). */
export type ClipAddSource = {
	source_s3_key: string;
	source_duration: number;
	start: number;
	end: number;
};

type SetterType = {
	setSelectedBoardId: (boardId: string | null) => void;
	setBoard: (board: BoardInfo) => void;
	clearBoard: () => void;
	setClip: (clip: BoardClipResponse) => void;
	onUpdateBoardClip?: (payload: UpdateBoardClipDonePayload) => void;
	setCurrentPanel: (currentPanel: number) => void;
	setFormData: SetStoreFunction<AddBoardFormData>;
	setErrors: Setter<Record<string, string[]>>;
	setMoveClipTrim: (range: MoveClipTrim) => void;
	/** Persist last-used board for quick add (skipped in studio). */
	rememberLastBoardId: (boardId: string) => void;
	/** Add a new clip or move an existing one into `targetBoardId`. */
	saveClipToBoard: (
		targetBoardId: string,
	) => Promise<
		| { ok: true; payload: UpdateBoardClipDonePayload }
		| { ok: false; reason: SaveClipFailureReason }
	>;
	/** Remember board, update context board, and notify parent. */
	finishClipSave: (
		payload: UpdateBoardClipDonePayload,
		board?: BoardInfo,
	) => void;
};

const INITIAL_STORE_SETTER: SetterType = {
	setSelectedBoardId: () => {},
	setBoard: (_board) => {},
	clearBoard: () => {},
	setClip: (_clip) => {},
	onUpdateBoardClip: (_payload) => {},
	setCurrentPanel: (_currentPanel) => {},
	setFormData: (() => {}) as SetStoreFunction<AddBoardFormData>,
	setErrors: (() => {}) as Setter<Record<string, string[]>>,
	setMoveClipTrim: () => {},
	rememberLastBoardId: () => {},
	saveClipToBoard: async () => ({ ok: false, reason: "missing_video" }),
	finishClipSave: () => {},
};

export const AddToBoardContext = createContext<[StoreType, SetterType]>([
	{
		selectedBoardId: null,
		board: null,
		clip: null,
		video: { id: "" },
		clipAddSource: null,
		moveClipTrimByItemId: {},
		moveClipTrim: { start: 0, end: undefined },
		search: {
			search: () => "",
			searchInput: () => "",
			handleSearch: () => {},
			handleClear: () => {},
		},
		currentPanel: () => 0,
		formData: { title: "", description: "", privacy: "private", icon: "" },
		errors: () => ({}),
		isLoading: false,
		isClipUnsaved: true,
		boardsList: undefined as unknown as Resource<ListBoardsResponse>,
	} as StoreType,
	INITIAL_STORE_SETTER,
]);
